import {
  Box,
  Button,
  IconButton,
  TextField,
  DialogTitle,
  DialogContent,
  DialogActions,
  styled,
  Dialog,
  Tooltip,
} from "@mui/material";
import { CopyToClipboard } from "react-copy-to-clipboard";
import CloseIcon from "@mui/icons-material/Close";
import styles from "./ElasticSearch.module.css";
import { useEffect, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { Row, Col } from "react-bootstrap";
import { ColDef } from "ag-grid-enterprise";
import { getElasticSearchData } from "../../api/elasticSearchApi/elasticSearchApi";
import {
  ElasticSearchModel,
  ElasticSearchModelInfo,
} from "../../types/ElasticSearchModel";
import SearchIcon from "@mui/icons-material/Search";
import PushPinIcon from "@mui/icons-material/PushPin";
import ClearIcon from "@mui/icons-material/Clear";
import ExcelIcon from "../../assets/images/ExcelExport.png"; // Adjust the path as necessary
import { UserGridPreferenceModel } from "../../types/UserGridPreferenceModel";
import {
  getUserGridPreference,
  saveUserGridPreference,
} from "../../api/userDataApi/userGridPreferenceAPI";
import NavigationBar from "../NavigationBar/NavigationBarComponent";
import "ag-grid-enterprise/styles/ag-grid.css";
import "ag-grid-enterprise/styles/ag-theme-quartz.min.css";
import { useSnackbar } from "../Common/SnackbarContext";
import { useLoader } from "../../context/LoaderContext";
import { appSettings } from "../../config/appSetting";
import CustomLoadingOverlay from "../CustomLoadingOverlay/CustomLoadingOverlay ";
import moment from "moment";
import LocalStorageUtil from "../../utils/localStorage";
import { FormatDate, FormatDateWithoutTime } from "../Common/CommonMethods";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

/* LinkButtonRenderer component moved outside ElasticSearch component */
interface LinkButtonRendererProps {
  data: any;
  onClickOpen: (data: any) => void;
}

const LinkButtonRenderer: React.FC<LinkButtonRendererProps> = ({
  data,
  onClickOpen,
}) => {
  if (!data) {
    return null; // Return null instead of an empty string for cleaner JSX
  }

  return (
    <button
      type="button"
      style={{
        background: "none",
        border: "none",
        color: "var(--aggrid-text-color)",
        textDecoration: "underline",
        cursor: "pointer",
        padding: 0,
        textAlign: "left",
      }}
      onClick={() => onClickOpen(data)}
    >
      View payload
    </button>
  );
};

const ElasticSearch = () => {
  const { setLoading } = useLoader();
  const pageSizeSelector = appSettings.agGridPageSizeSelector;
  const defaulPageSize = appSettings.agGridDefaulPageSize;
  const { showSnackbar } = useSnackbar();
  const [resetShow, setResetShow] = useState(false);
  const gridRef = useRef<AgGridReact>(null);
  const [open, setOpen] = useState(false);
  const [jsonPayloadValue, setJsonPayloadValue] = useState("");
  const [copyValue, setCopyValue] = useState(false);
  const [rowData, setRowData] = useState<ElasticSearchModelInfo[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [gridRecordExist, setGridRecordExist] = useState(false);

  const handleClickOpen = (props: any) => {
    setJsonPayloadValue(props.jsonPayload);
    setOpen(true);
  };
  const handleClose = () => {
    setCopyValue(false);
    setOpen(false);
  };

  const handleCopyToClipboard = () => {
    setCopyValue(true);
  };

  const linkButtonCellRenderer = (params: { data: any }) => (
    <LinkButtonRenderer data={params.data} onClickOpen={handleClickOpen} />
  );

  //Defined grid column
  const [columnDef] = useState<ColDef[]>([
    {
      headerName: "#",
      width: 75,
      sortable: false,
      filter: false,
      resizable: false,
      colId: "col1",
      valueGetter: (params) => {
        return params.node ? (params.node.rowIndex ?? 0) + 1 : 0;
      },
      cellStyle: () => {
        return { borderright: "1px solid #dee2e6" };
      },
    },
    {
      headerName: "Document ID",
      field: "documentID",
      width: 180,
      hide: false,
      colId: "col2",
      enableRowGroup: true,
      filter: true,
    },
    {
      headerName: "Document Type",
      field: "documentType",
      width: 180,
      hide: false,
      colId: "col3",
      enableRowGroup: true,
      filter: true,
    },
    {
      headerName: "Document Date",
      field: "documentDate",
      width: 180,
      hide: false,
      colId: "col4",
      enableRowGroup: true,
      filter: true,
    },
    {
      headerName: "FileName",
      field: "fileName",
      width: 150,
      hide: false,
      colId: "col5",
      enableRowGroup: true,
      filter: true,
    },
    {
      headerName: "CreationDateTime",
      field: "creationDateTime",
      width: 220,
      hide: false,
      colId: "col6",
      enableRowGroup: true,
      filter: true,
    },
    {
      headerName: "DDPageCount",
      field: "ddPageCount",
      width: 220,
      hide: false,
      colId: "col7",
      enableRowGroup: true,
      filter: true,
    },
    {
      headerName: "CUSIP",
      field: "cusip",
      width: 170,
      hide: false,
      colId: "col8",
      enableRowGroup: true,
      filter: true,
    },
    {
      headerName: "DDDocumentURL",
      field: "ddDocumentURL",
      width: 270,
      hide: false,
      colId: "col9",
      enableRowGroup: true,
      filter: true,
    },
    {
      headerName: "ThumbnailURL",
      field: "thumbnailURL",
      width: 290,
      hide: false,
      colId: "col10",
      enableRowGroup: true,
      filter: true,
    },
    {
      headerName: "Effectivenessdate",
      field: "effectivenessdate",
      width: 150,
      hide: false,
      colId: "col11",
      enableRowGroup: true,
      filter: true,
      valueFormatter: (params) => {
        return params.value ? FormatDate(params.value) : "";
      },
    },
    {
      headerName: "DocumentStatus",
      field: "documentStatus",
      width: 300,
      hide: false,
      colId: "col12",
      enableRowGroup: true,
      filter: true,
    },
    {
      headerName: "PreFlightResultStatus",
      field: "preFlightResultStatus",
      width: 150,
      hide: false,
      colId: "col13",
      enableRowGroup: true,
      filter: true,
    },
    {
      headerName: "SubSystemName",
      field: "subSystemName",
      hide: false,
      colId: "col14",
      enableRowGroup: true,
      filter: true,
    },
    {
      headerName: "Level1",
      field: "Level1",
      width: 150,
      hide: false,
      colId: "col15",
      enableRowGroup: true,
      filter: true,
    },
    {
      headerName: "RejectionReason",
      field: "",
      width: 170,
      hide: false,
      colId: "col16",
      enableRowGroup: true,
      filter: true,
    },
    {
      headerName: "qcDocID",
      field: "qcDocID",
      width: 170,
      hide: false,
      colId: "col17",
      enableRowGroup: true,
      filter: true,
    },
    {
      headerName: "APF Return Status",
      field: "apfreturnstatus",
      width: 170,
      hide: false,
      colId: "col18",
      enableRowGroup: true,
      filter: true,
    },
    {
      headerName: "OPF Return Status",
      field: "opfreturnstatus",
      width: 170,
      hide: false,
      colId: "col19",
      enableRowGroup: true,
      filter: true,
    },
    {
      headerName: "VIS Return Status",
      field: "visreturnstatus",
      width: 170,
      hide: false,
      colId: "col20",
      enableRowGroup: true,
      filter: true,
    },
    {
      headerName: "AssociationID",
      field: "associationID",
      width: 170,
      hide: false,
      colId: "colAID",
      enableRowGroup: true,
      filter: true,
    },
    {
      headerName: "Supplement Date",
      field: "supplementDate",
      width: 170,
      hide: false,
      colId: "colSupplementDate",
      enableRowGroup: true,
      valueFormatter: (params) => {
        return params.value ? FormatDateWithoutTime(params.value) : "";
      },

    },
    {
      headerName: "Revised Date",
      field: "revisedDate",
      width: 170,
      hide: false,
      colId: "colRevisedDate",
      enableRowGroup: true,
      filter: true,
      valueFormatter: (params) => {
        return params.value ? FormatDateWithoutTime(params.value) : "";
      },

    },
    {
      headerName: "Amendment Date",
      field: "amendmentDate",
      width: 170,
      hide: false,
      colId: "colAmendmentDate",
      enableRowGroup: true,
      filter: true,
      valueFormatter: (params) => {
        return params.value ? FormatDateWithoutTime(params.value) : "";
      },
    },
    {
      headerName: "Json Payload",
      field: "jsonPayload",
      cellRenderer: linkButtonCellRenderer,
      pinned: "right",
      lockPinned: true,
      suppressSizeToFit: true,
      width: 160,
      minWidth: 160,
      hide: false,
      colId: "colJSONPayload",
      sortable: false,
      filter: false,
      resizable: false,
      enableRowGroup: false,
    },
  ]);

  const handleInputChange = (event: any) => {
    setSearchValue(event.target.value);
    setResetShow(true);
  };

  const params: ElasticSearchModel = {
    SearchTerm: searchValue,
    SortColumn: "",
    SortOrder: 0,
    CurrentPage: 1,
    TotalRecords: 5,
    IsexporttpExcel: true,
    BusinessUnitID: Number(LocalStorageUtil.getItem("businessUnitId")),
  };
  const getData = async () => {
    setLoading(true);
    try {
      const elasticSearchModelInfo = await getElasticSearchData(params);
      setRowData(elasticSearchModelInfo.searchResults);
      if (elasticSearchModelInfo.searchResults.length === 0) {
        setGridRecordExist(false);
        showSnackbar("0 records found");
      } else {
        setGridRecordExist(true);
        showSnackbar("Grid state applied successfully.");
        const gridPreferences = await getUserGridPreferences();
        if (gridPreferences) {
          applyGridPreferences(gridPreferences);
        }
      }
    } catch (error) {
      showSnackbar("Error fetching data", true);
    } finally {
      setLoading(false);
    }
  };

  // Export data to excel
  const handleExport = () => {
    if (gridRef.current) {
      gridRef.current.api.exportDataAsExcel({
        fileName:
          "ElasticSearchReports_" + moment().format("YYYYMMDD") + ".xlsx",
        sheetName: "ElasticSearch Report",
        columnKeys: columnDef
          .map((colDef) => colDef.colId)
          .filter(
            (colId): colId is string =>
              colId !== "colJSONPayload" && colId !== undefined,
          ),
        processCellCallback: function (params) {
          return params.value;
        },
      });
    }
  };

  //Reapply grid state on component update
  useEffect(() => {
    const reapplyGridState = async () => {
      const gridPreferences = await getUserGridPreferences();
      if (gridPreferences) {
        applyGridPreferences(gridPreferences);
      }
    };
    reapplyGridState();
  }, [rowData]);

  //Save grid preferences
  const handleSaveGridPreferences = async () => {
    setLoading(true);
    if (gridRef.current) {
      const gridState = gridRef.current.api.getColumnState();
      const userGridPreference: UserGridPreferenceModel = {
        Area: "ElasticSearch",
        UserID: Number(LocalStorageUtil.getItem("userId")),
        BusinessUnitID: Number(LocalStorageUtil.getItem("businessUnitId")),
        ContextName: LocalStorageUtil.getItem("contextName")?.toString(),
        GridBlob: JSON.stringify(gridState),
        LastModifiedBy: "DocDistro-UserGridPreferencesAPI",
        LastModifiedDate: new Date().toISOString(),
      };

      try {
        const elasticSearchModelInfo =
          await saveUserGridPreference(userGridPreference);
        if (elasticSearchModelInfo) {
          showSnackbar("Grid state successfully saved.");
        }
      } catch (error) {
        showSnackbar("Error fetching data", true);
      } finally {
        setLoading(false);
      }
    }
  };

  const getUserGridPreferences = async () => {
    const userGridPreference = {
      Area: "ElasticSearch",
      UserID: Number(LocalStorageUtil.getItem("userId")),
      BusinessUnitID: Number(LocalStorageUtil.getItem("businessUnitId")),
      ContextName: LocalStorageUtil.getItem("contextName")?.toString(),
    };

    try {
      const elasticSearchModelInfo =
        await getUserGridPreference(userGridPreference);
      if (elasticSearchModelInfo) {
        showSnackbar("Grid state successfully saved.");
      }
      return elasticSearchModelInfo;
    } catch (error) {
      showSnackbar("Error fetching data", true);
    } finally {
      setLoading(false);
    }
  };

  //Apply grid preferences
  const applyGridPreferences = (gridPreferences: any) => {
    if (gridPreferences) {
      try {
        const gridState = JSON.parse(gridPreferences);
        if (gridRef.current) {
          gridRef.current.api.applyColumnState({
            state: gridState,
            applyOrder: true,
          });
        }
      } catch (error) {
        showSnackbar("Error fetching data", true);
      }
    } else {
      showSnackbar("No grid preferences found or GridBlob is undefined.", true);
    }
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      getData();
    }
  };

  const handleReset = () => {
    setSearchValue("");
    setResetShow(false);
    setRowData([]);
  };

  return (
    <div>
      <NavigationBar
        menuTitle="Search"
        screenTitle=""
        title="Elastic Search"
        text="Elastic Search"
      />
      <Row style={{ margin: "1em" }}>
        <Col xs={12}>
          <Box>
            <div>
              <Box
                component="form"
                sx={{ "& .MuiTextField-root": { m: 1, width: "50ch" } }}
                noValidate
                autoComplete="off"
                style={{
                  marginTop: "-1.5em",
                }}
              >
                <TextField
                  id="txtElasticSearch"
                  variant="outlined"
                  fullWidth
                  style={{
                    padding: "0px",
                    margin: "0px",
                    display: "none",
                  }}
                  value={searchValue}
                  onChange={handleInputChange}
                  onKeyDown={handleSearchKeyPress}
                  InputProps={{
                    style: { backgroundColor: "white", height: "35px" },
                  }}
                ></TextField>

                <input
                  type="text"
                  id="filter-text-box"
                  value={searchValue}
                  className={styles.txtSearch}
                  onInput={handleInputChange}
                  onKeyDown={handleSearchKeyPress}
                />
                <Tooltip title="Please click to search">
                  <SearchIcon
                    onClick={getData}
                    style={{
                      margin: ".65em 0em 1em .1em",
                      color: "var(--text-color)",
                      height: "1em",
                      width: "1em",
                    }}
                  />
                </Tooltip>
                {resetShow && (
                  <Tooltip title="Please click to Reset">
                    <ClearIcon
                      onClick={handleReset}
                      style={{
                        margin: ".7em 0em 1em .1em",
                        color: "var(--text-color)",
                        height: "1em",
                        width: "1em",
                      }}
                    />
                  </Tooltip>
                )}
                {gridRecordExist && (
                  <div
                    style={{
                      float: "right",
                      marginRight: "20px",
                      marginTop: "10px",
                    }}
                  >
                    <Tooltip title="Click to save grid state">
                      <IconButton
                        onClick={handleSaveGridPreferences}
                        className="preference-icon"
                      >
                        <PushPinIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Click to export the grid content in excel">
                      <button
                        type="button"
                        onClick={handleExport}
                        style={{
                          background: "none",
                          border: "none",
                          padding: 0,
                          cursor: "pointer",
                        }}
                        aria-label="Export to Excel"
                      >
                        <img
                          src={ExcelIcon}
                          alt="Doc-Distro"
                          className="excel-icon"
                        />
                      </button>
                    </Tooltip>
                  </div>
                )}
              </Box>
            </div>
            <div
              className="ag-theme-quartz-dark"
              style={{ height: "73vh", width: "100%" }}
            >
              <AgGridReact
                rowData={rowData}
                columnDefs={columnDef}
                pagination={true}
                paginationPageSize={defaulPageSize}
                paginationPageSizeSelector={pageSizeSelector}
                rowGroupPanelShow="always"
                pivotPanelShow="always"
                ref={gridRef}
                headerHeight={30}
                sideBar={{ toolPanels: ["columns", "filters"] }}
                onGridReady={async (params) => {
                  const gridPreferences = await getUserGridPreferences();
                  if (gridPreferences) {
                    applyGridPreferences(gridPreferences);
                  }
                }}
                loading={!rowData}
                loadingOverlayComponent={CustomLoadingOverlay}
                overlayNoRowsTemplate={
                  '<span class="ag-overlay-loading-center">No rows to show</span>'
                }
              />
            </div>
            <div>
              <Dialog
                open={open}
                fullWidth={true}
                maxWidth="xl"
                PaperProps={{ sx: { width: 400, height: 550 } }}
              >
                <DialogTitle
                  style={{
                    backgroundColor: "var(--primary-color) ",
                    height: "5em", // Set the background color of the dialog title
                  }}
                >
                  <div
                    className="col-sm-12"
                    style={{
                      display: "flex",
                      justifyContent: "left",
                    }}
                  >
                    <div
                      className="col-sm-11"
                      style={{
                        display: "flex",
                        alignItems: "left",
                        gap: "7px",
                        color: "var(--aggrid-text-color)",
                        fontSize: "1.5em",
                      }}
                    >
                      Json Payload
                    </div>
                    <div
                      className="col-sm-1"
                      style={{
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "right",
                      }}
                    >
                      {" "}
                      <Tooltip title="Close">
                        <IconButton
                          onClick={handleClose}
                          style={{
                            cursor: "pointer",
                            alignItems: "flex-end",
                            color: "var(--aggrid-text-color)",
                            width: "20px",
                            marginTop: "-5px",
                          }}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                </DialogTitle>
                <DialogContent dividers>
                  <TextField
                    multiline
                    aria-label="minimum height"
                    maxRows={26}
                    style={{ width: "100%", overflow: "auto" }}
                    value={jsonPayloadValue}
                    aria-readonly
                    InputProps={{
                      style: {
                        backgroundColor: "#f9f9f9",
                        border: "1px solid #ccc",
                        font: "menu",
                        fontFamily: "monospace",
                      },
                    }}
                  ></TextField>
                  {copyValue ? (
                    <span style={{ color: "green", fontSize: "14px" }}>
                      {" "}
                      Copied text to clipboard.
                    </span>
                  ) : null}
                </DialogContent>
                <DialogActions>
                  <CopyToClipboard
                    text={jsonPayloadValue}
                    onCopy={handleCopyToClipboard}
                  >
                    <Button
                      className={styles.copytoclipboard}
                      style={{
                        textTransform: "none",
                        borderRadius: "0px",
                        background: "rgb(77, 123, 167) !important",
                        color: "#ddd !important",
                        fontSize: "11px",
                        padding: "5px 12px",
                        border: "1px solid #144979",
                        margin: "0 5px 5px 5px",
                        whiteSpace: "white-space",
                      }}
                    >
                      Copy to clipboard
                    </Button>
                  </CopyToClipboard>
                </DialogActions>
              </Dialog>
            </div>
          </Box>
        </Col>
      </Row>
    </div>
  );
};

export default ElasticSearch;
